/**
 * Navigation Item Component
 * Renders individual menu items with support for:
 * - Icons, badges, keyboard shortcuts
 * - Active state highlighting
 * - Nested children (accordion)
 * - Pin/unpin functionality
 */

import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { NavigationTreeNode } from '../../types/navigation.types';
import { useNavigationStore } from '../../store/navigationStore';
import { cn } from '../../lib/utils';

interface NavigationItemProps {
  node: NavigationTreeNode;
  depth?: number;
}

export function NavigationItem({ node, depth = 0 }: NavigationItemProps) {
  const location = useLocation();
  const {
    toggleSection,
    togglePin,
    addToRecent,
    compactMode,
    sidebarCollapsed,
  } = useNavigationStore();

  const [isHovered, setIsHovered] = useState(false);

  // Get icon component
  const IconComponent = node.icon
    ? (Icons[node.icon as keyof typeof Icons] as any)
    : null;

  // Check if current route matches this item
  const isActive = node.path ? location.pathname === node.path : false;

  // Check if item is pinned
  const isPinned = useNavigationStore((state) =>
    state.pinnedItems.includes(node.id)
  );

  // Handle click
  const handleClick = () => {
    if (node.children && node.children.length > 0) {
      // Toggle accordion
      toggleSection(node.id);
    } else if (node.path) {
      // Track recent access
      addToRecent(node.id);
    }
  };

  // Handle pin toggle
  const handlePinToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    togglePin(node.id);
  };

  // Don't render if not visible
  if (!node.visible) {
    return null;
  }

  // Calculate padding based on depth
  const paddingLeft = sidebarCollapsed ? 'pl-4' : `pl-${4 + depth * 4}`;

  // Is this a sub-group header? (has children, no path, depth 0)
  const isGroupHeader = !node.path && !!node.children && node.children.length > 0 && depth === 0;

  // Base classes
  const baseClasses = cn(
    'group relative flex items-center gap-3 px-3 rounded-lg transition-all duration-200',
    paddingLeft,
    isGroupHeader
      ? [
          'py-1.5 mt-2',
          'text-xs font-semibold uppercase tracking-wide',
          'text-gray-500 dark:text-gray-400',
          node.enabled && 'hover:text-gray-700 dark:hover:text-gray-200',
        ]
      : [
          'py-2',
          'text-sm',
          'hover:bg-gray-100 dark:hover:bg-gray-800',
          {
            'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 font-medium':
              isActive,
            'text-gray-700 dark:text-gray-300': !isActive && node.enabled,
            'text-gray-400 dark:text-gray-600 cursor-not-allowed': !node.enabled,
            'cursor-pointer': node.enabled,
          },
        ]
  );

  // Icon classes
  const iconClasses = cn(
    'flex-shrink-0',
    isGroupHeader
      ? 'w-3.5 h-3.5 text-gray-400 dark:text-gray-500'
      : cn(compactMode ? 'w-4 h-4' : 'w-5 h-5', {
          'text-primary-600 dark:text-primary-400': isActive,
          'text-gray-500 dark:text-gray-400': !isActive,
        })
  );

  // Render item content
  const itemContent = (
    <>
      {/* Icon */}
      {IconComponent && (
        <IconComponent className={iconClasses} aria-hidden="true" />
      )}

      {/* Label */}
      {!sidebarCollapsed && (
        <span className="flex-1 truncate">
          {node.mobileLabel && window.innerWidth < 768
            ? node.mobileLabel
            : node.label}
        </span>
      )}

      {/* Badge */}
      {!sidebarCollapsed && node.badgeValue && (
        <span
          className={cn(
            'flex-shrink-0 px-2 py-0.5 text-xs font-medium rounded-full',
            node.badge?.color || 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
          )}
        >
          {node.badgeValue}
        </span>
      )}

      {/* Expand/Collapse Icon */}
      {!sidebarCollapsed && node.hasVisibleChildren && (
        <Icons.ChevronRight
          className={cn(
            'flex-shrink-0 transition-transform duration-200',
            isGroupHeader ? 'w-3 h-3' : 'w-4 h-4',
            node.isExpanded && 'rotate-90'
          )}
          aria-hidden="true"
        />
      )}

      {/* Pin Icon (on hover) */}
      {!sidebarCollapsed && isHovered && !node.children && (
        <button
          onClick={handlePinToggle}
          className="flex-shrink-0 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
          aria-label={isPinned ? 'Unpin item' : 'Pin item'}
        >
          <Icons.Pin
            className={cn('w-3 h-3', isPinned && 'fill-current')}
            aria-hidden="true"
          />
        </button>
      )}

      {/* Keyboard Shortcut */}
      {!sidebarCollapsed &&
        node.keyboardShortcut &&
        isHovered &&
        !node.children && (
          <span className="flex-shrink-0 text-xs text-gray-400 dark:text-gray-500 font-mono">
            {node.keyboardShortcut}
          </span>
        )}
    </>
  );

  return (
    <div>
      {/* Main Item */}
      {node.path ? (
        <Link
          to={node.path}
          className={baseClasses}
          onClick={handleClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          aria-label={node.ariaLabel}
          aria-current={isActive ? 'page' : undefined}
          aria-disabled={!node.enabled}
        >
          {itemContent}
        </Link>
      ) : (
        <button
          className={cn(baseClasses, 'w-full text-left')}
          onClick={handleClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          aria-label={node.ariaLabel}
          aria-expanded={node.isExpanded}
          aria-disabled={!node.enabled}
          disabled={!node.enabled}
        >
          {itemContent}
        </button>
      )}

      {/* Children (Accordion) */}
      {node.hasVisibleChildren && node.isExpanded && (
        <div
          className={cn(
            'mt-1 space-y-1',
            !sidebarCollapsed && 'ml-2 border-l-2 border-gray-200 dark:border-gray-700'
          )}
        >
          {node.children?.map((child) => (
            <NavigationItem key={child.id} node={child} depth={depth + 1} />
          ))}
        </div>
      )}

      {/* Divider */}
      {node.dividerAfter && !sidebarCollapsed && (
        <div className="my-2 border-t border-gray-200 dark:border-gray-700" />
      )}
    </div>
  );
}

/**
 * Collapsed Navigation Item (Icon Only)
 * Shown when sidebar is collapsed
 */
export function CollapsedNavigationItem({ node }: { node: NavigationTreeNode }) {
  const location = useLocation();
  const { addToRecent } = useNavigationStore();

  const IconComponent = node.icon
    ? (Icons[node.icon as keyof typeof Icons] as any)
    : null;

  const isActive = node.path ? location.pathname === node.path : false;

  const handleClick = () => {
    if (node.path) {
      addToRecent(node.id);
    }
  };

  if (!node.visible || !IconComponent) {
    return null;
  }

  const content = (
    <div
      className={cn(
        'relative flex items-center justify-center w-12 h-12 rounded-lg transition-all duration-200',
        'hover:bg-gray-100 dark:hover:bg-gray-800',
        {
          'bg-primary-50 dark:bg-primary-900/20': isActive,
        }
      )}
    >
      <IconComponent
        className={cn('w-5 h-5', {
          'text-primary-600 dark:text-primary-400': isActive,
          'text-gray-500 dark:text-gray-400': !isActive,
        })}
        aria-hidden="true"
      />
      {node.badgeValue && (
        <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
          {node.badgeValue}
        </span>
      )}
    </div>
  );

  if (node.path) {
    return (
      <Link
        to={node.path}
        onClick={handleClick}
        aria-label={node.ariaLabel}
        title={node.label}
        className="block"
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      onClick={handleClick}
      aria-label={node.ariaLabel}
      title={node.label}
      className="block"
      disabled={!node.enabled}
    >
      {content}
    </button>
  );
}

// Made with Bob
